import { Helpers } from '../../../../utility/helpers';
import { pickProductPricePromotion } from '../../../common/components/atoms/ProductPrice/ProductPricePromotionPicker';
import type { ProductPresentationLabel } from '../../../ecommerce/components/ProductPresentationSpecs';
import { prePurchaseProductPricePresentationLogic } from '../molecules/PrepurchaseProductPriceLabels/PrepurchaseProductPriceLabels';
import {
    type CartItemPriceLabelsParamaters,
    type CartPrimaryPriceSpec,
    type CartSecondaryPriceSpec,
    type CartTotalPriceSpec,
    cartItemPriceLabels,
} from './CartItemProductPresentationSpec';

const getPrePurchaseSecondaryPriceLabelForSpec =
    (params: CartItemPriceLabelsParamaters) =>
    (spec: CartSecondaryPriceSpec): ProductPresentationLabel | null => {
        const picked = pickProductPricePromotion({
            promo: params.product?.promotion,
            checkUserMemberContext: false,
        });

        // do not show any secondary labels if the promotion price is higher than historical price
        if (picked.promotion?.higherThanHistoricalPrice && params.isPILPricesLogicEnabled) {
            return null;
        }

        switch (spec) {
            case 'historicalPrice': {
                if (!params.arePILPricesEnabled) {
                    return null;
                }

                if (params.isB2B || picked.type === 'multiBuy') {
                    // B2B specific logic for historical price
                    return null;
                }

                return cartItemPriceLabels.getSecondaryPriceLabelForSpec(params)(spec);
            }
            case 'originalPrice': {
                if (params.arePILPricesEnabled && params.isPILPricesLogicEnabled) {
                    // if product has historical price data we do not want to show regular price and regular comparative price with the exception of if it has max usage or is a member promo.
                    // but since we dont show historical price for B2B, we can show original prices. EASY RIGHT?! RIGHT?!
                    if (
                        params.product.historicalPriceData &&
                        !picked.promotion?.maxUseText &&
                        !picked.promotion?.isMemberPrice &&
                        !params.isB2B
                    ) {
                        return null;
                    }

                    return cartItemPriceLabels.getSecondaryPriceLabelForSpec(params)(spec);
                }

                return cartItemPriceLabels.getSecondaryPriceLabelForSpec(params)(spec);
            }
            default: {
                return cartItemPriceLabels.getSecondaryPriceLabelForSpec(params)(spec);
            }
        }
    };

const getPrePurchaseTotalPriceLabelForSpec =
    (params: CartItemPriceLabelsParamaters) =>
    (spec: CartTotalPriceSpec): ProductPresentationLabel | null => {
        const picked = pickProductPricePromotion({
            promo: params.product?.promotion,
            checkUserMemberContext: false,
        });

        switch (spec) {
            // if the promotion price is higher than historical price, we should always show total price as black label
            case 'totalPrice': {
                if (picked.promotion?.higherThanHistoricalPrice) {
                    return cartItemPriceLabels.createTotalPriceLabel({
                        totalPriceData: params.cartItem.totalPriceData,
                        priceType: params.priceType,
                        theme: 'black',
                        a11yTextType: params.a11yTextType,
                    });
                }

                return cartItemPriceLabels.getTotalPriceLabelForSpec(params)(spec);
            }
            case 'recycleFee':
            case 'excludingTax': {
                return cartItemPriceLabels.getTotalPriceLabelForSpec(params)(spec);
            }
            default:
                return Helpers.exhaustiveCheck(spec);
        }
    };

const getPrePurchasePrimaryPriceLabelForSpec =
    ({
        product,
        a11yTextType,
        priceType,
        isUserMember,
        isPILPricesLogicEnabled,
    }: CartItemPriceLabelsParamaters) =>
    (spec: CartPrimaryPriceSpec): ProductPresentationLabel | null => {
        switch (spec) {
            case 'primaryPrice': {
                return prePurchaseProductPricePresentationLogic.getPrimaryPricePresentation({
                    product,
                    priceType,
                    a11yTextType,
                    isUserMember,
                    isPILPricesLogicEnabled,
                });
            }
            default:
                return Helpers.exhaustiveCheck(spec);
        }
    };

const cartItemPrePurchasePriceLabels = {
    getPrePurchasePrimaryPriceLabelForSpec,
    getPrePurchaseSecondaryPriceLabelForSpec,
    getPrePurchaseTotalPriceLabelForSpec,
};

export default cartItemPrePurchasePriceLabels;
